import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, MapPin, ShoppingBag, Play } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import { api, errMsg, media, timeAgo } from "@/lib/api";
import { Avatar, Img } from "@/components/common";
import ReportDialog from "@/components/ReportDialog";

const k = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n);

export function usePostActions(post, onChange) {
  const { requireAuth } = useApp();
  const patch = (d) => onChange({ ...post, ...d });
  return {
    like: requireAuth(async () => {
      const on = !post.liked; patch({ liked: on, likes_count: post.likes_count + (on ? 1 : -1) });
      try { await api.post(`/posts/${post.id}/like`); } catch (e) { toast.error(errMsg(e)); }
    }),
    save: requireAuth(async () => {
      const r = await api.post(`/posts/${post.id}/save`); patch({ saved: r.data.saved });
      toast(r.data.saved ? "Saved to your collection 🔖" : "Removed from saved");
    }),
    share: async () => {
      const url = `${window.location.origin}/post/${post.id}`;
      api.post(`/posts/${post.id}/share`);
      if (navigator.share) { try { await navigator.share({ title: post.title, url }); } catch { /* cancelled */ } }
      else { navigator.clipboard?.writeText(url); toast.success("Post link copied"); }
      patch({ shares_count: post.shares_count + 1 });
    },
    follow: requireAuth(async () => {
      const r = await api.post(`/users/${post.user_id}/follow`); patch({ following: r.data.following });
      toast(r.data.following ? `Following ${post.user?.name}` : "Unfollowed");
    }),
  };
}

export function PostMenu({ post, onChange, onDeleted }) {
  const { user, requireAuth } = useApp();
  const [report, setReport] = useState(false);
  const mine = user && user.id === post.user_id;
  const edit = async () => {
    const c = window.prompt("Edit caption", post.caption);
    if (c == null) return;
    await api.patch(`/posts/${post.id}`, { caption: c }); onChange({ ...post, caption: c }); toast.success("Caption updated");
  };
  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/posts/${post.id}`); toast.success("Post deleted"); onDeleted?.(post.id);
  };
  const block = requireAuth(async () => {
    const r = await api.post(`/users/${post.user_id}/block`); toast(r.data.blocked ? "User blocked. You won't see their posts." : "User unblocked"); onDeleted?.(post.id);
  });
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={`post-menu-${post.id}`} className="rounded-full p-1 hover:bg-[#F8F1E5]"><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem asChild><Link to={`/profile/${post.user_id}`}>View profile</Link></DropdownMenuItem>
          {mine ? (<>
            <DropdownMenuItem onClick={edit} data-testid="post-edit-caption">Edit caption</DropdownMenuItem>
            <DropdownMenuItem onClick={del} data-testid="post-delete" className="text-red-600">Delete post</DropdownMenuItem>
          </>) : (<>
            <DropdownMenuItem onClick={requireAuth(() => setReport(true))} data-testid="post-report">Report post</DropdownMenuItem>
            <DropdownMenuItem onClick={block} data-testid="post-block-user">Block user</DropdownMenuItem>
          </>)}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReportDialog open={report} onClose={() => setReport(false)} type="post" id={post.id} />
    </>
  );
}

export function Actions({ post, a, big }) {
  const s = big ? "h-6 w-6" : "h-5 w-5";
  return (
    <div className="flex items-center gap-4 text-[#57534E]">
      <button onClick={a.like} data-testid="post-like-btn" className="flex items-center gap-1 text-xs font-semibold transition-transform active:scale-90">
        <Heart className={`${s} ${post.liked ? "fill-[#E11D48] text-[#E11D48]" : ""}`} />{k(post.likes_count)}</button>
      <Link to={`/post/${post.id}`} data-testid="post-comment-btn" className="flex items-center gap-1 text-xs font-semibold"><MessageCircle className={s} />{k(post.comments_count)}</Link>
      <button onClick={a.share} data-testid="post-share-btn" className="flex items-center gap-1 text-xs font-semibold"><Share2 className={s} />{k(post.shares_count)}</button>
      <button onClick={a.save} data-testid="post-save-btn" className="ml-auto"><Bookmark className={`${s} ${post.saved ? "fill-[#D97706] text-[#D97706]" : ""}`} /></button>
    </div>
  );
}

export default function PostCard({ post: initial, onDeleted, tall }) {
  const [post, setPost] = useState(initial);
  const a = usePostActions(post, setPost);
  const { user } = useApp();
  const u = post.user || {};
  return (
    <article data-testid={`post-card-${post.id}`} className="card fade-up overflow-hidden">
      <div className="flex items-start gap-2 p-2.5 sm:gap-2.5 sm:p-3">
        <Link to={`/profile/${post.user_id}`} className="shrink-0"><Avatar src={u.avatar} name={u.name} size="h-8 w-8 sm:h-9 sm:w-9" /></Link>
        <div className="min-w-0 flex-1 leading-tight">
          <Link to={`/profile/${post.user_id}`} className="block truncate whitespace-nowrap text-[13px] font-semibold hover:text-[#6B1D2F] sm:text-sm" title={u.name}>{u.name}{u.verified && <span className="ml-1 text-[#D97706]">✦</span>}</Link>
          {user?.id !== post.user_id && (
            <button onClick={a.follow} data-testid="post-follow-btn" className={`mt-0.5 block text-xs font-bold ${post.following ? "text-[#A8A29E]" : "text-[#D97706]"}`}>{post.following ? "Following" : "+ Follow"}</button>
          )}
          <span className="mt-0.5 flex items-center gap-1 overflow-hidden text-[11px] text-[#78716C]"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{post.city} · {timeAgo(post.created_at)}</span></span>
        </div>
        <div className="-mr-1 shrink-0 self-start"><PostMenu post={post} onChange={setPost} onDeleted={onDeleted} /></div>
      </div>
      <Link to={`/post/${post.id}`} className="img-zoom relative block">
        {post.images?.[0] ? <Img src={media(post.images[0])} alt={post.title} ratio={tall ? "aspect-[4/5]" : "aspect-square"} />
          : <video src={media(post.video)} className="aspect-square w-full object-cover" muted />}
        {post.festival && <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#6B1D2F] backdrop-blur">{post.festival.replace(/-/g, " ")}</span>}
        {post.images?.length > 1 && <span className="absolute right-3 top-3 rounded-full bg-black/50 px-2 py-0.5 text-[10px] text-white">1/{post.images.length}</span>}
        {post.video && <Play className="absolute bottom-3 right-3 h-7 w-7 rounded-full bg-black/50 p-1.5 text-white" />}
        {post.products?.length > 0 && (
          <span data-testid="shop-this-look-btn" className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-[#6B1D2F]/90 px-3 py-1.5 text-[11px] font-semibold text-white backdrop-blur">
            <ShoppingBag className="h-3.5 w-3.5" />Shop this look · {post.products.length}</span>
        )}
      </Link>
      <div className="space-y-2 p-3">
        <Actions post={post} a={a} />
        <Link to={`/post/${post.id}`} className="block">
          <p className="font-display text-base font-semibold leading-snug">{post.title}</p>
          <p className="line-clamp-2 text-xs text-[#78716C]">{post.caption}</p>
        </Link>
      </div>
    </article>
  );
}
